import { useState } from "react";
import { api, ApiClientError } from "../../lib/apiClient.js";
import type { JobDetailDto } from "@grizzly/shared";
import { Card } from "../ui/card.js";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";
import { OperationsEditor, type OperationDraft } from "./operationsEditor.js";
import { MaterialsEditor, type MaterialDraft } from "./materialsEditor.js";
import type { CatalogOptions } from "../../lib/useCatalogOptions.js";

interface Fields {
  title: string;
  description: string;
  customerId: string;
  quantity: string;
  dueDate: string;
  priority: string;
}

const EMPTY_FIELDS: Fields = {
  title: "",
  description: "",
  customerId: "",
  quantity: "1",
  dueDate: "",
  priority: "medium",
};

export function JobCreateForm({
  catalog,
  onCreated,
  onCancel,
}: {
  catalog: CatalogOptions;
  onCreated(job: JobDetailDto): void;
  onCancel(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [fields, setFields] = useState<Fields>(EMPTY_FIELDS);
  const [operations, setOperations] = useState<OperationDraft[]>([]);
  const [materials, setMaterials] = useState<MaterialDraft[]>([]);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof Fields>(key: K, value: Fields[K]) =>
    setFields((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.createJob({
        title: fields.title,
        description: fields.description,
        customerId: fields.customerId || "",
        quantity: fields.quantity,
        dueDate: fields.dueDate,
        priority: fields.priority,
        operations: operations
          .filter((o) => o.description.trim())
          .map((o, idx) => ({
            sequence: idx,
            workCenterId: o.workCenterId || "",
            description: o.description,
            setupMinutes: Number(o.setupMinutes || "0"),
            runMinutesPerUnit: Number(o.runMinutesPerUnit || "0"),
          })),
        materials: materials
          .filter((m) => m.materialId)
          .map((m) => ({
            materialId: m.materialId,
            plannedQuantity: m.plannedQuantity || "1",
          })),
      });
      push(t("jobs.toast.created"), `${res.job.number} — ${res.job.title}`);
      onCreated(res.job);
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="max-w-3xl">
      <div className="space-y-3">
        <TextInput
          label={t("common.fields.title")}
          required
          value={fields.title}
          onChange={(e) => set("title", e.target.value)}
        />
        <TextInput
          label={t("common.fields.description")}
          value={fields.description}
          onChange={(e) => set("description", e.target.value)}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectInput
            label={t("common.fields.customer")}
            options={[
              { value: "", label: t("jobs.create.noneInternal") },
              ...catalog.customers.map((c) => ({
                value: c.id,
                label: c.displayName || c.legalName,
              })),
            ]}
            value={fields.customerId}
            onChange={(e) => set("customerId", e.target.value)}
          />
          <TextInput
            label={t("common.fields.quantity")}
            required
            value={fields.quantity}
            onChange={(e) => set("quantity", e.target.value)}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="job-due" className="text-sm font-medium text-ink">
              {t("common.fields.dueDate")}
            </label>
            <input
              id="job-due"
              type="date"
              value={fields.dueDate}
              onChange={(e) => set("dueDate", e.target.value)}
              className="h-11 rounded-[var(--radius-card)] border border-line bg-surface-raised px-3 text-sm"
            />
          </div>
          <SelectInput
            label={t("common.fields.priority")}
            options={[
              { value: "low", label: t("status.priority.low") },
              { value: "medium", label: t("status.priority.medium") },
              { value: "high", label: t("status.priority.high") },
            ]}
            value={fields.priority}
            onChange={(e) => set("priority", e.target.value)}
          />
        </div>

        <OperationsEditor
          operations={operations}
          workCenters={catalog.workCenters}
          onChange={setOperations}
        />
        <MaterialsEditor
          materials={materials}
          catalog={catalog.materials}
          onChange={setMaterials}
        />

        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={submit}
            disabled={busy || !fields.title.trim() || !fields.quantity}
          >
            {busy ? t("common.creating") : t("jobs.create.button")}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
